import type { ContractRolePresetView, ParticipantType } from "@closer/shared";
import { findPermission, PARTICIPANT_PERMISSION_CEILINGS, PARTICIPANT_TYPES } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * KATILIMCI ROL PRESETLERİ (CLOS-149 `[130]`).
 *
 * ### ⚠ Preset YETKİ KATMANI DEĞİL
 *
 * Sayfa bunu cümleyle yazıyor: preset "bu tür katılımcıya şu seti ver" önerisini
 * adlandırır; gerçek yetki permission motoru ve kayıt erişimi kararlarıyla uygulanır.
 * Atama anında açık sete çözülür — sonradan preseti değiştirmek verilmiş erişimleri
 * DEĞİŞTİRMEZ ve ekran bunu da söylüyor.
 *
 * ### ⚠ Tavan KATILIMCI TÜRÜNDEN
 *
 * Seçilebilir yetkiler `PARTICIPANT_PERMISSION_CEILINGS`'ten geliyor; haricî türlerde
 * liste dar. Ekran tam kataloğu gösterip sunucunun reddetmesini beklemiyor: kullanıcı
 * asla verilemeyecek bir yetkiyi işaretleyip "neden olmadı" diye sormuyor.
 *
 * ### ⚠ Katılımcı türü SONRADAN DEĞİŞMİYOR
 *
 * Güncelleme şeması türü almıyor (tavan tür üzerinden kuruluyor); form da düzenlemede
 * türü kilitliyor. Değişebilseydi, mevcut set yeni tavanın dışında kalabilirdi.
 *
 * ### ⚠ Silme YOK, ARŞİVLEME var — ve arşivlenen LİSTEDEN DÜŞER
 *
 * Preset geçmişte kullanılmış olabilir; satırı yok etmek "bu erişim hangi şablondan
 * geldi" sorusunu cevapsız bırakırdı. Kayıt duruyor ama uç arşivlenmişleri süzüyor
 * (`isNull(archivedAt)`): liste bir SEÇİM kaynağı, arşiv onun işi değil. Elle testte
 * doğrulandı — arşivlenen satır listeden kayboluyor.
 */
export function ParticipantRolesPage() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();

  const [formAcik, setFormAcik] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState<ContractRolePresetView | null>(null);
  const [ad, setAd] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [tur, setTur] = useState<ParticipantType>("internal");
  const [gorunurluk, setGorunurluk] = useState<"internal" | "shared">("internal");
  const [secili, setSecili] = useState<string[]>([]);

  const yonetebilir = can("organization.role");

  const anahtar = tenantKey(tenantId, "contract-role-presets");
  const presetler = useQuery({
    queryKey: anahtar,
    queryFn: async () =>
      (await api<ContractRolePresetView[]>("/api/contract-role-presets", { tenantId })).data,
  });

  const yenile = () => queryClient.invalidateQueries({ queryKey: anahtar });

  function formuKapat() {
    setFormAcik(false);
    setDuzenlenen(null);
    setAd("");
    setAciklama("");
    setTur("internal");
    setGorunurluk("internal");
    setSecili([]);
  }

  const kaydet = useMutation({
    mutationFn: () => {
      const govde = {
        name: ad,
        ...(aciklama.trim() ? { description: aciklama.trim() } : {}),
        permissions: secili,
        visibility: gorunurluk,
      };
      return duzenlenen
        ? api(`/api/contract-role-presets/${duzenlenen.id}`, {
            method: "PUT",
            tenantId,
            /** ⚠ Okunan sürüm gövdede: eşzamanlı düzenlemede sessiz ezme yerine 409. */
            body: JSON.stringify({ ...govde, version: duzenlenen.version }),
          })
        : api("/api/contract-role-presets", {
            method: "POST",
            tenantId,
            body: JSON.stringify({ ...govde, participantType: tur }),
          });
    },
    onSuccess: async () => {
      formuKapat();
      await yenile();
      toast.success(duzenlenen ? "Katılımcı rolü güncellendi" : "Katılımcı rolü tanımlandı");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Katılımcı rolü kaydedilemedi"),
  });

  const arsivle = useMutation({
    mutationFn: (presetId: string) =>
      api(`/api/contract-role-presets/${presetId}/archive`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({}),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Katılımcı rolü arşivlendi");
    },
    onError: (hata: unknown) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Katılımcı rolü arşivlenemedi"),
  });

  function duzenlemeyeAl(preset: ContractRolePresetView) {
    setDuzenlenen(preset);
    setFormAcik(true);
    setAd(preset.name);
    setAciklama(preset.description ?? "");
    setTur(preset.participantType);
    setGorunurluk(preset.visibility);
    setSecili(preset.permissions);
  }

  /** ⚠ Seçilebilir set TÜRÜN TAVANI: kataloğun tamamı değil. */
  const tavan = PARTICIPANT_PERMISSION_CEILINGS[tur];

  const TUR_ADLARI: Record<ParticipantType, string> = {
    internal: "İç katılımcı",
    external_person: "Haricî kişi",
    external_organization: "Haricî organizasyon",
  };

  return (
    <div className="flex w-full flex-col gap-5">
      <PageHeader
        title="Katılımcı Rolleri"
        description="Sözleşmeye çağırdığınız kişiye önerilecek yetki setlerini adlandırın."
        actions={
          yonetebilir && !formAcik ? (
            <Button type="button" size="sm" onClick={() => setFormAcik(true)}>
              Yeni katılımcı rolü
            </Button>
          ) : null
        }
      />

      <Card>
        <div className="flex flex-col gap-3">
          {/* ⚠ Preset YETKİ KATMANI DEĞİL: kullanıcı bunu çıkarımla öğrenmiyor. */}
          <p className="text-sm text-fg-muted">
            Katılımcı rolü, sözleşmeye çağırdığınız kişiye hangi yetki setinin önerileceğini
            adlandırır. Yetki atama anında açık sete çözülür; rolü sonradan değiştirmek daha önce
            verilmiş erişimleri değiştirmez.
          </p>

          {formAcik ? (
            <div className="flex flex-col gap-2 rounded-md border border-border-default p-3">
              <FormField label="Rol adı" required>
                <Input id="preset-ad" value={ad} onChange={(olay) => setAd(olay.target.value)} />
              </FormField>

              <FormField label="Açıklama">
                <Textarea
                  id="preset-aciklama"
                  rows={2}
                  value={aciklama}
                  onChange={(olay) => setAciklama(olay.target.value)}
                />
              </FormField>

              {/* ⚠ Tür düzenlemede KİLİTLİ: tavan tür üzerinden kuruluyor. */}
              <FormField
                label="Katılımcı türü"
                hint={duzenlenen ? "Tanımlı rolün katılımcı türü değiştirilemez." : undefined}
              >
                <Select
                  id="preset-tur"
                  disabled={duzenlenen !== null}
                  value={tur}
                  onChange={(olay) => {
                    const yeni = olay.target.value as ParticipantType;
                    setTur(yeni);
                    /** Tür değişince tavan dışına düşen seçimler temizleniyor. */
                    setSecili((onceki) =>
                      onceki.filter((k) => PARTICIPANT_PERMISSION_CEILINGS[yeni].includes(k)),
                    );
                  }}
                >
                  {PARTICIPANT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {TUR_ADLARI[t]}
                    </option>
                  ))}
                </Select>
              </FormField>

              <FormField label="Görünürlük">
                <Select
                  id="preset-gorunurluk"
                  value={gorunurluk}
                  onChange={(olay) => setGorunurluk(olay.target.value as "internal" | "shared")}
                >
                  <option value="internal">Yalnız iç ekipler</option>
                  <option value="shared">Karşı tarafla paylaşılan yüzeylerde de</option>
                </Select>
              </FormField>

              <fieldset className="flex flex-col gap-1">
                <legend className="text-sm font-medium text-fg-strong">Yetkiler</legend>
                {tavan.map((anahtarDegeri) => (
                  <Checkbox
                    key={anahtarDegeri}
                    label={findPermission(anahtarDegeri)?.description ?? anahtarDegeri}
                    checked={secili.includes(anahtarDegeri)}
                    onChange={() =>
                      setSecili((onceki) =>
                        onceki.includes(anahtarDegeri)
                          ? onceki.filter((k) => k !== anahtarDegeri)
                          : [...onceki, anahtarDegeri],
                      )
                    }
                  />
                ))}
              </fieldset>

              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={formuKapat}>
                  Vazgeç
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={!ad.trim() || secili.length === 0}
                  loading={kaydet.isPending}
                  onClick={() => kaydet.mutate()}
                >
                  Kaydet
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      <Card>
        {presetler.isPending ? (
          <div role="status" aria-label="Katılımcı rolleri yükleniyor">
            <Skeleton lines={3} />
          </div>
        ) : presetler.error ? (
          <StateView
            state={resolveErrorState(presetler.error, { idempotent: true })}
            onRetry={() => void presetler.refetch()}
          />
        ) : (presetler.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-soft">Tanımlı katılımcı rolü yok.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(presetler.data ?? []).map((preset) => (
              <li
                key={preset.id}
                className="flex flex-col gap-2 rounded-md border border-border-default p-3"
              >
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-fg-strong">{preset.name}</span>
                  <Badge tone="neutral">{TUR_ADLARI[preset.participantType]}</Badge>
                  <Badge tone={preset.accessLevel === "write" ? "warning" : "neutral"}>
                    {preset.accessLevel === "write" ? "Yazma erişimi" : "Okuma erişimi"}
                  </Badge>
                </div>

                {preset.description ? (
                  <p className="text-sm text-fg">{preset.description}</p>
                ) : null}

                <p className="text-xs text-fg-soft">
                  {preset.permissions.map((k) => findPermission(k)?.description ?? k).join(" · ")}
                </p>

                {/*
                  ⚠ Liste YALNIZ AKTİF presetleri taşıyor: uç arşivlenmişleri süzüyor
                  (`isNull(archivedAt)`). Bu yüzden burada "arşivde" durumu için ayrı bir
                  dal yok — olsaydı hiç çalışmayan, testi de gerçek yanıtla eşleşmeyen ölü
                  bir kod olurdu. Arşivlenen satır kayıttan silinmiyor, listeden düşüyor.
                */}
                {yonetebilir ? (
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => duzenlemeyeAl(preset)}
                    >
                      Düzenle
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => arsivle.mutate(preset.id)}
                    >
                      Arşivle
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
